import api from "../../../src/services/feApi.ts";
import { ApiResponse } from "../../types/ApiResponse";
import {
  CurrencyDto,
  PaymentTypeDto,
  ProductCombinationDto,
  ProductExtrasDto,
  OrderHistoryGuestGroupDto,
} from "../../types/sale/sale";
import { SalePayDto } from "../../types/sale/payData";

type ApiErrorResponse = {
  Messages?: string[];
  Errors?: string[];
};

// ---------------------------------------------------
// Type Guards (No any, No errors)
// ---------------------------------------------------
function isApiError(
  error: unknown
): error is {
  response: { status: number; data: ApiErrorResponse };
} {
  if (
    typeof error === "object" &&
    error !== null &&
    "response" in error
  ) {
    const resp = (error as Record<string, unknown>).response;
    return (
      typeof resp === "object" &&
      resp !== null &&
      "status" in (resp as Record<string, unknown>)
    );
  }
  return false;
}

function isNetworkError(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    "request" in error &&
    !("response" in error)
  );
}

// ---------------------------------------------------
// SALE SERVICE
// ---------------------------------------------------
export const SaleService = {
  // ---------------------------------------------------
  // GET CURRENCY
  // ---------------------------------------------------
  getCurrency: async (): Promise<CurrencyDto | null> => {
    try {
      const response = await api.get<ApiResponse<CurrencyDto>>(
        "/Sales/list/currency"
      );

      const result = response.data;

      if (!result.Success || result.StatusCode !== 200) {
        console.warn(
          "Failed to fetch currency list:",
          result.Errors || result.Messages
        );
        return null;
      }

      return result.Data;
    } catch (error: unknown) {
      if (isApiError(error)) {
        const { status, data } = error.response;

        console.error("Server responded with error:", status, data);

        throw new Error(
          data?.Messages?.[0] ??
            data?.Errors?.[0] ??
            "Unexpected server error occurred."
        );
      }

      if (isNetworkError(error)) {
        throw new Error("No response from server. Please check your connection.");
      }

      throw new Error(
        error instanceof Error ? error.message : "Unexpected error occurred."
      );
    }
  },

  // ---------------------------------------------------
  // GET PAYMENT TYPES
  // ---------------------------------------------------
  getPaymentTypes: async (): Promise<PaymentTypeDto[] | null> => {
    try {
      const response = await api.get<ApiResponse<PaymentTypeDto[]>>(
        "/Sales/list/payment/types"
      );

      const result = response.data;

      if (!result.Success || result.StatusCode !== 200) {
        console.warn(
          "Failed to fetch payment types:",
          result.Errors || result.Messages
        );
        return null;
      }

      return result.Data ?? [];
    } catch (error: unknown) {
      if (isApiError(error)) {
        const { status, data } = error.response;

        console.error("Server responded with error:", status, data);

        throw new Error(
          data?.Messages?.[0] ??
            data?.Errors?.[0] ??
            "Unexpected server error occurred."
        );
      }

      if (isNetworkError(error)) {
        throw new Error("No response from server. Check your network connection.");
      }

      throw new Error(
        error instanceof Error ? error.message : "Unexpected error occurred."
      );
    }
  },

listProductExtras: async (productId: number): Promise<ProductExtrasDto[] | null> => {
  try {
    const response = await api.post<ApiResponse<ProductExtrasDto[]>>(
      "/Sales/list/product/extras",
      { ProductID: productId },
      {
        headers: { "Content-Type": "application/json" },
      }
    );

    const result = response.data;

    if (!result.Success || result.StatusCode !== 200) {
      console.warn(
        "Failed to fetch product extras:",
        result.Errors || result.Messages
      );
      return null;
    }

    return result.Data ?? [];
  } catch (error: unknown) {
    if (isApiError(error)) {
      const { status, data } = error.response;

      console.error("Server responded with error:", status, data);

      throw new Error(
        data?.Messages?.[0] ??
          data?.Errors?.[0] ??
          "Unexpected server error occurred."
      );
    }

    if (isNetworkError(error)) {
      throw new Error("No response from server. Check your network connection.");
    }

    throw new Error(
      error instanceof Error ? error.message : "Unexpected error occurred."
    );
  }
},

listOrderHistory: async (
  accountId: string
): Promise<OrderHistoryGuestGroupDto[] | null> => {
  try {
    const response = await api.post<ApiResponse<OrderHistoryGuestGroupDto[]>>(
      "/Sales/list/order/history",
      { FK_AccountID: accountId },
      {
        headers: { "Content-Type": "application/json" },
      }
    );

    const result = response.data;

    if (!result.Success || result.StatusCode !== 200) {
      console.warn(
        "Failed to fetch order history:",
        result.Errors || result.Messages
      );
      return null;
    }

    return result.Data ?? [];
  } catch (error: unknown) {
    if (isApiError(error)) {
      const { status, data } = error.response;

      console.error("Server responded with error:", status, data);

      throw new Error(
        data?.Messages?.[0] ??
          data?.Errors?.[0] ??
          "Unexpected server error occurred."
      );
    }

    if (isNetworkError(error)) {
      throw new Error("No response from server. Check your network connection.");
    }

    throw new Error(
      error instanceof Error ? error.message : "Unexpected error occurred."
    );
  }
},

reorderTabLine: async (
  tabLineId: number
): Promise<Response | null> => {
  try {
    const response = await api.post<ApiResponse<Response>>(
      "/Sales/tab/line/reorder",
      { TabLineID: tabLineId },
      {
        headers: { "Content-Type": "application/json" },
      }
    );

    const result = response.data;

    if (!result.Success || result.StatusCode !== 200) {
      console.warn(
        "Failed to reorder tab line:",
        result.Errors || result.Messages
      );
      return null;
    }

    return result.Data ?? null;
  } catch (error: unknown) {
    if (isApiError(error)) {
      const { status, data } = error.response;

      console.error("Server responded with error:", status, data);

      throw new Error(
        data?.Messages?.[0] ??
          data?.Errors?.[0] ??
          "Unexpected server error occurred."
      );
    }

    if (isNetworkError(error)) {
      throw new Error("No response from server. Check your network connection.");
    }

    throw new Error(
      error instanceof Error ? error.message : "Unexpected error occurred."
    );
  }
},


  // ---------------------------------------------------
  // PAY SALE
  // ---------------------------------------------------
  paySale: async (
    rec: Omit<SalePayDto, "id">
  ): Promise<SalePayDto | null> => {
    try {
      const response = await api.post<ApiResponse<SalePayDto>>(
        "/Sales/pay/tab",
        rec,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const result = response.data;

      if (!result.Success || result.StatusCode !== 200) {
        console.warn("Pay sale failed:", result.Errors || result.Messages);
        return null;
      }

      return result.Data;
    } catch (error: unknown) {
      if (isApiError(error)) {
        const { status, data } = error.response;

        console.error("Server responded with error:", status, data);

        throw new Error(
          data?.Messages?.[0] ??
            data?.Errors?.[0] ??
            "Unexpected server error occurred."
        );
      }

      if (isNetworkError(error)) {
        throw new Error("No response from server. Please check your connection.");
      }

      throw new Error(
        error instanceof Error ? error.message : "Unexpected error occurred."
      );
    }
  },

  // ---------------------------------------------------
  // GET PRODUCT COMBINATIONS
  // ---------------------------------------------------
  getProductCombinations: async (
    productId: number
  ): Promise<ProductCombinationDto[] | null> => {
    try {
      const payload = { FK_ProductID: productId };

      const response = await api.post<ApiResponse<ProductCombinationDto[]>>(
        "/Sales/list/combinations",
        payload,
        {
          headers: { "Content-Type": "application/json" },
        }
      );

      const result = response.data;

      if (!result.Success || result.StatusCode !== 200) {
        console.warn(
          "Failed to fetch product combinations:",
          result.Errors || result.Messages
        );
        return null;
      }

      return result.Data ?? [];
    } catch (error: unknown) {
      if (isApiError(error)) {
        const { status, data } = error.response;

        console.error("Server responded with error:", status, data);

        throw new Error(
          data?.Messages?.[0] ??
            data?.Errors?.[0] ??
            "Unexpected server error occurred."
        );
      }

      if (isNetworkError(error)) {
        throw new Error("No response from server. Check your connection.");
      }

      throw new Error(
        error instanceof Error ? error.message : "Unexpected error occurred."
      );
    }
  },
};
